import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import Toast from '../components/Toast';
import { SectionLabel, MiniPill } from '../components/ui';

const DOC_TYPE_OPTIONS = [
  { value: 'tax_code_notice', label: 'Tax Code Notice' },
  { value: 'hmrc_letter', label: 'HMRC Letter' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_LABELS = Object.fromEntries(DOC_TYPE_OPTIONS.map(o => [o.value, o.label]));

const inputCls =
  'w-full rounded-md border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 text-[14px] focus:border-brand-500 focus:ring-brand-500';

function getExt(name = '') {
  return (name.split('.').pop() || '').toLowerCase();
}

function FileTypeTile({ name }) {
  const ext = getExt(name);
  const styles = {
    pdf: { label: 'PDF', bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-300' },
    jpg: { label: 'JPG', bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300' },
    jpeg: { label: 'JPG', bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300' },
    png: { label: 'PNG', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300' },
  };
  const s = styles[ext] || { label: ext.toUpperCase() || 'FILE', bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-300' };
  return (
    <span className={`w-9 h-9 rounded-md grid place-items-center font-bold text-[10px] tracking-wider flex-shrink-0 ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function formatSizeKB(bytes) {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function bucketDocs(docs) {
  const now = new Date();
  const week = new Date(now); week.setDate(week.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const buckets = { week: [], month: [], earlier: [] };
  for (const d of docs) {
    const t = d.uploaded_at ? new Date(d.uploaded_at) : null;
    if (!t) { buckets.earlier.push(d); continue; }
    if (t >= week) buckets.week.push(d);
    else if (t >= monthStart) buckets.month.push(d);
    else buckets.earlier.push(d);
  }
  return buckets;
}

function UploadIcon() {
  return (
    <svg
      className="mx-auto mb-2 text-brand-700"
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function DocRow({ doc }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-brand-50/40 dark:hover:bg-brand-900/10 transition-colors">
      <FileTypeTile name={doc.file_name} />
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-ink dark:text-gray-100 truncate">{doc.file_name}</p>
        <p className="text-[12px] text-gray-500 dark:text-gray-400">
          {DOC_TYPE_LABELS[doc.document_type] || doc.document_type} · {formatRelative(doc.uploaded_at)}
        </p>
      </div>
      {doc.google_drive_url ? (
        <a
          href={doc.google_drive_url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-2.5 py-1.5 rounded-md text-[12px] font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 whitespace-nowrap"
        >
          Open in Drive ↗
        </a>
      ) : (
        <MiniPill tone="gray">Local</MiniPill>
      )}
    </div>
  );
}

function BucketHeading({ label }) {
  return (
    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [dragActive, setDragActive] = useState(false);

  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState('tax_code_notice');
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const res = await api.get('/documents/my-documents');
      setDocuments(res.data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const addFiles = (newFiles) => {
    const valid = newFiles.filter(f => {
      const ext = getExt(f.name);
      if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
        setToast({ message: `${f.name}: only PDF, JPG, and PNG files are accepted`, type: 'error' });
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        setToast({ message: `${f.name}: file is too large (5MB max)`, type: 'error' });
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    addFiles(selected);
    // reset native input so picking the same file again still fires onChange
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = Array.from(e.dataTransfer.files);
    addFiles(dropped);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) formData.append('documents', file);
      formData.append('document_type', docType);
      if (notes.trim()) formData.append('notes', notes.trim());

      await api.post('/documents/company-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setToast({ message: `${files.length} document(s) uploaded successfully`, type: 'success' });
      setFiles([]);
      setNotes('');
      setDocType('tax_code_notice');
      await loadDocuments();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to upload document', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const total = documents.length;
  const buckets = useMemo(() => bucketDocs(documents), [documents]);
  const clusterByRecency = total > 10;

  return (
    <div className="max-w-[1100px] mx-auto px-8 py-7">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      <header className="mb-6">
        <h1 className="font-heading text-[28px] text-ink dark:text-gray-100 leading-none">Documents</h1>
        <p className="text-[14.5px] text-gray-500 dark:text-gray-400 mt-2 max-w-[640px]">
          Send HMRC letters, tax code notices, and anything else your accountant should see. Files are stored securely in our shared drive.
        </p>
      </header>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
        {/* Upload card */}
        <div className="bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-xl p-5">
          <SectionLabel>Upload a new document</SectionLabel>

          <div className="grid gap-3 mb-4 grid-cols-1 sm:grid-cols-2">
            <div>
              <label htmlFor="docType" className="text-[12px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">
                Document type
              </label>
              <select
                id="docType"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className={inputCls}
              >
                {DOC_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="docNotes" className="text-[12px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">
                Notes (optional)
              </label>
              <input
                id="docNotes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. April tax code changes"
                className={inputCls}
              />
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="doc-upload"
          />
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl py-10 text-center transition-colors cursor-pointer ${
              dragActive
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                : 'border-brand-300 dark:border-brand-700 bg-brand-50/30 dark:bg-brand-900/10 hover:bg-brand-50/60 dark:hover:bg-brand-900/20'
            }`}
          >
            <UploadIcon />
            <p className="text-[14.5px] font-semibold text-ink dark:text-gray-100">
              {dragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-1">
              or click to browse · PDF, JPG, PNG · max 5MB each
            </p>
          </div>

          {/* Selected files list */}
          {files.length > 0 && (
            <ul className="mt-4 space-y-2">
              {files.map((file, i) => (
                <li
                  key={`${file.name}-${i}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900/40 ring-1 ring-gray-200 dark:ring-gray-700"
                >
                  <FileTypeTile name={file.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold text-ink dark:text-gray-100 truncate">{file.name}</p>
                    <p className="text-[12px] text-gray-500 dark:text-gray-400 tabular-nums">{formatSizeKB(file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="px-2 py-1 rounded text-[12px] font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Footer row */}
          <div className={`mt-4 flex items-center justify-end gap-3 ${files.length === 0 ? 'opacity-70' : ''}`}>
            <p className="text-[12px] text-gray-500 dark:text-gray-400 italic mr-auto">
              {files.length === 0
                ? 'No files selected'
                : `${files.length} file${files.length === 1 ? '' : 's'} ready to upload`}
            </p>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className="px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-[13.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? 'Uploading…' : files.length > 0 ? `Upload ${files.length}` : 'Upload'}
            </button>
          </div>
        </div>

        {/* Previous uploads */}
        <div>
          <SectionLabel count={total}>Previous uploads</SectionLabel>
          {loading ? (
            <div className="bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-xl p-4 text-[13px] text-gray-500">
              Loading…
            </div>
          ) : total === 0 ? (
            <div className="bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-xl p-6 text-center">
              <p className="text-[13.5px] font-medium text-gray-700 dark:text-gray-300">No documents uploaded yet</p>
              <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-1">
                Anything you upload here lands with your accountant.
              </p>
            </div>
          ) : clusterByRecency ? (
            <div className="bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-xl overflow-hidden">
              {buckets.week.length > 0 && (
                <>
                  <BucketHeading label={`This week · ${buckets.week.length}`} />
                  {buckets.week.map(d => <DocRow key={d.id} doc={d} />)}
                </>
              )}
              {buckets.month.length > 0 && (
                <>
                  <BucketHeading label={`This month · ${buckets.month.length}`} />
                  {buckets.month.map(d => <DocRow key={d.id} doc={d} />)}
                </>
              )}
              {buckets.earlier.length > 0 && (
                <>
                  <BucketHeading label={`Earlier · ${buckets.earlier.length}`} />
                  {buckets.earlier.map(d => <DocRow key={d.id} doc={d} />)}
                </>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 rounded-xl overflow-hidden">
              {documents.map(d => <DocRow key={d.id} doc={d} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
